import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ToastContainer, toast } from 'react-toastify';
import { API_DETAILS, COOKIE_KEYS } from "../../App";

const MedicalRecords = (props) => {
  const uId = Cookies.get(COOKIE_KEYS.userId);

  const [carePlans, setCarePlans] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [loadError, setLoadError] = useState(false);

  // Defaults to the address on the account; editable so records can be sent
  // somewhere else without changing the sign-in email.
  const [email, setEmail] = useState("");
  const [accountEmail, setAccountEmail] = useState("");

  // Same care plans the home and education screens read, so this list cannot
  // disagree with what the patient sees elsewhere.
  useEffect(() => {
    if (!uId) return;

    const load = async () => {
      try {
        const [plansRes, userRes] = await Promise.all([
          axios.get(
            API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
            `/getAllCarePlanByPatient?patientId=${uId}`
          ),
          axios.get(
            API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
            `/getUser/${uId}`
          ),
        ]);

        setCarePlans(
          (plansRes.data || []).map((plan, index) => ({
            // The real carePlanId, so a selection still means the same plan when
            // the server rebuilds the list to fill the request. The array index
            // used before was only stable while the list was; adding a care plan
            // silently reassigned every selection.
            id: plan.carePlanId ?? index,
            title: plan.diagName,
            doctor: plan.doctorName,
            lastModified: plan.lastModified,
            followUp: plan.followUp,
          }))
        );

        const onAccount = userRes.data?.email || "";
        setAccountEmail(onAccount);
        setEmail(onAccount);
      } catch (err) {
        console.error("Failed to load medical records data:", err);
        setLoadError(true);
      }
    };

    load();
  }, [uId]);

  const togglePlan = (id) => {
    setSelectedPlans(prev =>
      prev.includes(id) ? prev.filter(planId => planId !== id) : [...prev, id]
    );
  };

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const [sending, setSending] = useState(false);

  const handleRequest = async () => {
    if (selectedPlans.length === 0 || !emailLooksValid || sending) return;

    setSending(true);
    try {
      const res = await axios.post(
        API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension +
        `/medical-records/request`,
        // The selection was collected and gated the button, but was never sent —
        // so every request returned the full record regardless of what was ticked.
        { userId: Number(uId), notifyEmail: email.trim(), carePlanIds: selectedPlans }
      );
      // The server decides where the link goes — always the account address — so
      // report what it actually did rather than what was typed in the field.
      toast.success(res.data?.message || "Records link sent.");
    } catch (err) {
      console.error("Medical records request failed:", err);
      toast.error("We couldn't send your records just now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: '896px', margin: '0 auto', padding: '32px', fontFamily: "'Arial', sans-serif" }}>
      <ToastContainer />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <button style={{
          padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center'
        }} onClick={() => props.openProfileMenu?.()}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>
          </svg>
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Medical Records</h2>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.875rem' }}>Request your medical records to be sent to your email</p>
        </div>
      </div>

      {/* Email Section */}
      <div style={{ padding: '24px', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 600 }}>Email Address</h3>
        <label htmlFor="records-email" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px', color: '#64748b' }}>
          Records will be sent to this email
        </label>
        <input
          id="records-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: '6px',
            border: `1px solid ${email && !emailLooksValid ? 'var(--youro-coral)' : '#e2e8f0'}`,
            backgroundColor: '#ffffff', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box'
          }}
        />
        {email && !emailLooksValid && (
          <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--youro-coral)' }}>
            That doesn't look like a valid email address.
          </p>
        )}
        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
          For your security the download link always goes to the address on your
          account{accountEmail ? ` (${accountEmail})` : ""}, and your records are
          never attached to an email. A different address here is notified that a
          request was made, without any link or medical detail.
        </p>
      </div>

      {/* Care Plans Selection */}
      <div style={{ padding: '24px', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 600 }}>Select Care Plans</h3>
        <p style={{ margin: '0 0 24px', opacity: 0.7, fontSize: '0.875rem' }}>Choose which care plans you'd like to include in your request</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {carePlans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => togglePlan(plan.id)}
              style={{
                padding: '16px', borderRadius: '8px', cursor: 'pointer', border: '1px solid',
                borderColor: selectedPlans.includes(plan.id) ? 'var(--youro-sage)' : '#e2e8f0',
                backgroundColor: selectedPlans.includes(plan.id) ? '#f0f4f0' : 'transparent',
                transition: 'all 0.2s ease', display: 'flex', gap: '16px', alignItems: 'flex-start'
              }}
            >
              <input
                type="checkbox"
                checked={selectedPlans.includes(plan.id)}
                readOnly
                style={{ marginTop: '4px', cursor: 'pointer' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--youro-sage)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>
                  </svg>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{plan.title}</h4>
                </div>
                <div style={{ fontSize: '0.875rem', opacity: 0.7, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {plan.doctor && <><span>{plan.doctor}</span> •</>}
                  {plan.lastModified && (
                    <span>
                      Updated{" "}
                      {new Date(plan.lastModified).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </span>
                  )}
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem',
                    backgroundColor: plan.followUp ? 'var(--youro-mint)' : 'var(--youro-sand)',
                    color: plan.followUp ? 'var(--youro-sage)' : 'var(--youro-charcoal)'
                  }}>
                    {plan.followUp ? 'Follow-up required' : 'No follow-up'}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {carePlans.length === 0 && (
            <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.7 }}>
              {loadError
                ? "We couldn't load your care plans. Please refresh to try again."
                : "You don't have any care plans yet. Once a provider creates one, it will appear here."}
            </p>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div
        style={{
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '24px',
          backgroundColor: 'var(--youro-mint)',
          border: '1px solid var(--youro-sage)'
        }}
      >
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: '600' }}>
          What's Included in Your Records
        </h4>
        <p style={{ margin: '0 0 12px', fontSize: '0.875rem' }}>
          Everything below covers only the care plans you select above — records
          for your other conditions are not included.
        </p>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          fontSize: '0.875rem'
        }}>
          <li>Your care plans and follow-up recommendations</li>
          <li>Symptom score assessments and tracking history for those conditions</li>
          <li>Prescription history and medication details</li>
          <li>Educational materials reviewed</li>
          <li>Appointments for those conditions</li>
        </ul>
        <p style={{ margin: '12px 0 0', fontSize: '0.8125rem', opacity: 0.75 }}>
          Clinical notes written by your provider are not included. Contact your
          care team if you need them.
        </p>
      </div>

      {/* Action Button */}
      <button
        disabled={selectedPlans.length === 0 || !emailLooksValid || sending}
        onClick={handleRequest}
        style={{
          width: '100%', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          cursor: selectedPlans.length === 0 || !emailLooksValid || sending ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlans.length === 0 || !emailLooksValid || sending ? 'var(--youro-stone)' : 'var(--youro-coral)',
          color: '#ffffff', border: 'none', transition: 'all 0.2s ease'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 15V3"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/>
        </svg>
        {sending
          ? "Sending…"
          : `Request Medical Records (${selectedPlans.length} selected)`}
      </button>
    </div>
  );
};

export default MedicalRecords;
